'use client'

import { HTMLProps, useRef, useState } from 'react'
import clsx from 'classnames'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useLocale } from 'use-intl'
import { useTranslations } from 'next-intl'
import { IMassMediaPost, IMassMediaPostCategory } from '@/interfaces/media'
import { SearchableSelect } from '@/shared/ui/select'
import { useRouter } from '@/routing'
import getMassMediaPosts, { GetMassMediaPostsParams } from '@/actions/getMassMediaPosts'
import { localeMap } from '@/shared/utils/directus'
import { MediaAboutAsyncList } from '@/features/media-about-list'
import { Pagination } from '@/shared/ui/pagination'
import style from './media-about-feed.module.scss'

type Option = {
  value: string
  label: string
}

type MediaAboutFeedProps = HTMLProps<HTMLDivElement> & {
  categories: IMassMediaPostCategory[]
  initData: { items: IMassMediaPost[]; page: number; limit: number; total: string | number }
}

export const MediaAboutFeed = (props: MediaAboutFeedProps) => {
  const { className, initData, categories } = props
  const { items, page, total, limit } = initData
  const currentLocale = useLocale()
  const [currentPage, setCurrentPage] = useState(page)
  const [pageSize, setPageSize] = useState(limit)
  const [totalCount, setTotalCount] = useState(total as number)
  const filterRef = useRef<string | null>(null)
  const [pages, setPages] = useState<Record<number, IMassMediaPost[]>>({ 0: items })
  const router = useRouter()

  const totalPageCount = Math.ceil(totalCount / pageSize)
  const parentRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: totalPageCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 900,
    overscan: 1,
  })
  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  const fetchData = ({ category, page }: GetMassMediaPostsParams) => {
    getMassMediaPosts({ locale: localeMap[currentLocale], category, page })
      .then(({ items, total, limit }) => {
        setPageSize(limit)
        setTotalCount(total as number)
        if (page) {
          setPages(prevState => ({ ...prevState, [page - 1]: items }))
        } else {
          setPages({ 0: items })
          setCurrentPage(1)
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const onChangeCategory = (option: Option | null) => {
    const category = option?.value || null
    filterRef.current = category
    fetchData({ category: category || undefined })
  }

  const onFetchPage = (page: number) => {
    const category = filterRef.current

    fetchData({ page, category: category || undefined })
  }

  const translate = useTranslations()

  return (
    <section
      className={clsx(style.container, 'main-padding', 'white-section white-section-js', className)}
      ref={parentRef}
    >
      <SearchableSelect
        className={style.select}
        options={categories.map(category => ({
          value: category.slug,
          label: category.name,
        }))}
        placeholder={translate('categories')}
        emptyMessage={translate('emptyMessage')}
        onChange={itemOption => {
          const option = itemOption as Option | null
          onChangeCategory(option)
        }}
      />

      {virtualItems?.length ? (
        <ul
          style={{
            height: totalSize,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualItems.map(virtualRow => (
            <li
              key={virtualRow.key}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow?.start ?? 0}px)`,
              }}
            >
              <MediaAboutAsyncList
                anchorId={`page${virtualRow.index}`}
                cards={pages[virtualRow.index]}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                onCurrentPage={setCurrentPage}
                onFetchPage={onFetchPage}
              />
            </li>
          ))}
        </ul>
      ) : (
        <MediaAboutAsyncList
          key="initMediaAboutAsyncList"
          anchorId={`page0`}
          cards={pages[0]}
          data-index={0}
          onCurrentPage={setCurrentPage}
        />
      )}

      <Pagination
        className={style.pagination}
        currentPage={currentPage}
        onPageChange={page => {
          location.href = `#page${page - 1}`
          setCurrentPage(page)
        }}
        pageSize={pageSize}
        totalPageCount={totalPageCount}
      />
    </section>
  )
}
